import { DEFAULT_DROPDOWN_INDEX, DROPDOWN_FIRST_INDEX, MAIN_DROPDOWN_GROUP_INDEX } from '../constant/product-option';
import { createSafeItemId } from '../core/item-card.core';
import type { CustomInputState, ProductOptionDispatch, ProductOptionState, SelectionState } from '../type/common.type';
import { isSameItemCard } from '../util/checkers/item-card-checker';

export const productOptionReducer = (state: ProductOptionState, action: ProductOptionDispatch): ProductOptionState => {
  switch (action.type) {
    case 'INITIALIZE_OPTION': {
      return action.payload;
    }

    case 'SET_OPTION_MAP': {
      return {
        ...state,
        option: {
          ...state.option,
          map: action.payload,
        },
      };
    }

    case 'RESET_SELECTION': {
      const selection_updated_index = action.payload;
      return {
        ...state,
        selection: initializeSelectionFromIndex(selection_updated_index),
      };
    }

    case 'CLOSE_ALL_DROPDOWNS': {
      return {
        ...state,
        dropdown_states: state.dropdown_states.map((row) => row.map(() => false)),
      };
    }

    case 'TOGGLE_DROPDOWN': {
      return {
        ...state,
        dropdown_states: toggleDropdown(state, action.payload.group_index, action.payload.dropdown_index),
      };
    }

    case 'TOGGLE_ADDITIONAL_DROPDOWN': {
      return {
        ...state,
        additional_dropdown_state: !state.additional_dropdown_state,
      };
    }

    case 'HANDLE_CLICK_DROPDOWN': {
      const { group_index, dropdown_index, closeAll = false } = action.payload;
      const updated_selection = { ...state.selection };
      const metadata = updated_selection.metadata;
      const should_revert_group_selection = state.selection.selected_group_index > group_index;
      const should_revert_dropdown_selection = state.selection.selected_dropdown_index > dropdown_index;

      if (should_revert_group_selection) {
        const previous_group_selection = metadata.previous_group_selections?.[group_index];
        updated_selection.selected_group_index = group_index;
        updated_selection.selected_dropdown_index = dropdown_index;

        // 메타데이터 리스트 업데이트 (이전 그룹 선택 정보에서 복원)
        sliceMetadataLists(metadata, dropdown_index, previous_group_selection);

        // 이전 그룹 선택 정보 정리
        if (metadata.previous_group_selections) {
          metadata.previous_group_selections = metadata.previous_group_selections.filter(
            (_, index) => index < group_index,
          );
        }
      } else if (should_revert_dropdown_selection) {
        updated_selection.selected_dropdown_index = dropdown_index;

        // 메타데이터 리스트 업데이트 (현재 메타데이터에서 자르기)
        sliceMetadataLists(metadata, dropdown_index);
      } else if (state.selection.selected_dropdown_index === DEFAULT_DROPDOWN_INDEX) {
        updated_selection.selected_group_index = group_index;
        updated_selection.selected_dropdown_index = state.selection.selected_dropdown_index + 1;
      }

      return {
        ...state,
        ui: {
          ...state.ui,
          is_item_card_mode_active: false,
        },
        dropdown_states: closeAll
          ? state.dropdown_states.map((row) => row.map(() => false))
          : toggleDropdown(state, group_index, dropdown_index),
        additional_dropdown_state: false,
        selection: updated_selection,
      };
    }

    case 'TOGGLE_SHIPPING_FILTER': {
      const { should_reset_selection } = action.payload;
      const updated_state = { ...state };
      const toggle_to_zigzin_only = !state.ui.is_only_zigzin_option_active;

      // 첫번째 드롭다운이 아니고, 직진 옵션이 없는 경우 초기화
      if (toggle_to_zigzin_only && should_reset_selection) {
        updated_state.dropdown_states = toggleDropdown(state, MAIN_DROPDOWN_GROUP_INDEX, DROPDOWN_FIRST_INDEX);
        updated_state.selection = initializeSelectionFromIndex(DROPDOWN_FIRST_INDEX);
      }

      return {
        ...updated_state,
        ui: {
          ...updated_state.ui,
          is_only_zigzin_option_active: toggle_to_zigzin_only,
        },
      };
    }

    case 'CONTINUE_OPTION_SELECTION': {
      const { group_index, dropdown_index, selection } = action.payload;
      return {
        ...state,
        dropdown_states: toggleDropdown(state, group_index, dropdown_index),
        selection,
      };
    }

    case 'ADD_ITEM_CARD': {
      const updated_dropdown_states = state.dropdown_states.map((row) => row.map(() => false));

      return {
        ...state,
        ui: {
          ...state.ui,
          is_item_card_mode_active: true,
          restock_notification_start_index: null,
          restock_notification_status: null,
        },
        dropdown_states: updated_dropdown_states,
        additional_dropdown_state: false,
        selection: {
          selected_group_index: MAIN_DROPDOWN_GROUP_INDEX,
          selected_dropdown_index: DEFAULT_DROPDOWN_INDEX,
          metadata: {
            selected_option_name_list: [],
            selected_zigzin_option_id_list: [],
            selected_general_option_id_list: [],
            last_selected_shipping_type: null,
            previous_group_selections: [],
          },
        },
        custom_input_states: resetCustomInputStates(state.custom_input_states),
        item_cards: action.payload,
      };
    }

    case 'REMOVE_ITEM_CARD': {
      const { item: targetItem } = action.payload;
      const updated_state = { ...state };

      // ID와 custom_input_list가 모두 일치하는 아이템만 제거
      updated_state.item_cards.list = state.item_cards.list.filter((item) => {
        // 동일한 아이템이면 제거 (false 반환), 다르면 유지 (true 반환)
        return !isSameItemCard(
          { id: createSafeItemId(item, item.bogo_item_list), custom_input_list: item.custom_input_list },
          {
            id: createSafeItemId(targetItem, targetItem.bogo_item_list),
            custom_input_list: targetItem.custom_input_list,
          },
        );
      });

      updated_state.item_cards.total_count = updated_state.item_cards.list.reduce((sum, item) => sum + item.count, 0);

      // 아이템 카드가 모두 제거된 경우 옵션 선택 화면으로
      if (!updated_state.item_cards.list.length) {
        if (state.enabled_first_dropdown_open) {
          updated_state.dropdown_states = state.dropdown_states.map((row, bogo_idx) =>
            row.map((_, option_idx) => bogo_idx === 0 && option_idx === 0),
          );
        }

        updated_state.ui.is_item_card_mode_active = false;
        updated_state.selection = {
          selected_group_index: MAIN_DROPDOWN_GROUP_INDEX,
          selected_dropdown_index: DROPDOWN_FIRST_INDEX,
          metadata: {
            selected_option_name_list: [],
            selected_zigzin_option_id_list: [],
            selected_general_option_id_list: [],
            last_selected_shipping_type: null,
            previous_group_selections: [],
          },
        };
      }
      return updated_state;
    }

    case 'INCREMENT_ITEM_COUNT': {
      const targetItem = action.payload;
      const updated_list = state.item_cards.list.map((item) => {
        const isSame = isSameItemCard(
          { id: createSafeItemId(item, item.bogo_item_list), custom_input_list: item.custom_input_list },
          {
            id: createSafeItemId(targetItem, targetItem.bogo_item_list),
            custom_input_list: targetItem.custom_input_list,
          },
        );
        return isSame ? { ...item, count: item.count + 1 } : item;
      });
      const total_count = updated_list.reduce((sum, item) => sum + item.count, 0);

      return {
        ...state,
        item_cards: {
          list: updated_list,
          total_count,
        },
      };
    }

    case 'DECREMENT_ITEM_COUNT': {
      const targetItem = action.payload;
      const updated_list = state.item_cards.list.map((item) => {
        const isSame = isSameItemCard(
          { id: createSafeItemId(item, item.bogo_item_list), custom_input_list: item.custom_input_list },
          {
            id: createSafeItemId(targetItem, targetItem.bogo_item_list),
            custom_input_list: targetItem.custom_input_list,
          },
        );
        return isSame ? { ...item, count: item.count - 1 } : item;
      });
      const total_count = updated_list.reduce((sum, item) => sum + item.count, 0);

      return {
        ...state,
        item_cards: {
          list: updated_list,
          total_count,
        },
      };
    }

    case 'UPDATE_RESTOCK_NOTIFICATION_START_INDEX': {
      return {
        ...state,
        ui: {
          ...state.ui,
          restock_notification_start_index: action.payload,
          is_restock_selection_mode: Number.isInteger(action.payload),
        },
      };
    }

    case 'UPDATE_WMS_NOTIFICATION_INFO': {
      const { product_item_id, active } = action.payload;
      if (!state.option.map?.path_index_map || !state.option.map?.item_list_map) {
        return state;
      }

      const updated_state = { ...state };
      const updated_option_map = updated_state.option.map;

      const updated_items_with_value_ids = new Set<string>();

      // zigzin path_index_map 업데이트 & value_id 수집
      if (updated_option_map.path_index_map.zigzin) {
        const updated_zigzin_map = new Map(updated_option_map.path_index_map.zigzin);
        Array.from(updated_zigzin_map.entries()).forEach(([path, item]) => {
          if (item.id === product_item_id) {
            updated_zigzin_map.set(path, {
              ...item,
              wms_notification_info: { active },
            });
            // 해당 아이템의 value_id들을 수집
            if (item.item_attribute_list) {
              const value_ids = item.item_attribute_list.map((attr) => attr.value_id).join(';');
              updated_items_with_value_ids.add(value_ids);
            }
          }
        });
        updated_option_map.path_index_map = {
          ...updated_option_map.path_index_map,
          zigzin: updated_zigzin_map,
        };
      }

      // matched path_index_map 업데이트 & value_id 수집
      if (updated_option_map.path_index_map.matched) {
        const updated_matched_map = new Map(updated_option_map.path_index_map.matched);
        Array.from(updated_matched_map.entries()).forEach(([path, item]) => {
          if (item.id === product_item_id) {
            updated_matched_map.set(path, {
              ...item,
              wms_notification_info: { active },
            });
            if (item.item_attribute_list) {
              const value_ids = item.item_attribute_list.map((attr) => attr.value_id).join(';');
              updated_items_with_value_ids.add(value_ids);
            }
          }
        });
        updated_option_map.path_index_map = {
          ...updated_option_map.path_index_map,
          matched: updated_matched_map,
        };
      }

      // item_list_map 업데이트
      if (updated_items_with_value_ids.size > 0) {
        const value_ids_array = Array.from(updated_items_with_value_ids);

        // zigzin item_list_map 업데이트
        if (updated_option_map.item_list_map?.zigzin) {
          const updated_zigzin_item_map = new Map(updated_option_map.item_list_map.zigzin);
          value_ids_array.forEach((value_ids) => {
            if (updated_zigzin_item_map.has(value_ids)) {
              const existing_item = updated_zigzin_item_map.get(value_ids)!;
              updated_zigzin_item_map.set(value_ids, {
                ...existing_item,
                wms_notification_info: { active },
              });
            }
          });
          updated_option_map.item_list_map = {
            ...updated_option_map.item_list_map,
            zigzin: updated_zigzin_item_map,
          };
        }

        // matched item_list_map 업데이트
        if (updated_option_map.item_list_map.matched) {
          const updated_matched_item_map = new Map(updated_option_map.item_list_map.matched);
          value_ids_array.forEach((value_ids) => {
            if (updated_matched_item_map.has(value_ids)) {
              const existing_item = updated_matched_item_map.get(value_ids)!;
              updated_matched_item_map.set(value_ids, {
                ...existing_item,
                wms_notification_info: { active },
              });
            }
          });
          updated_option_map.item_list_map = {
            ...updated_option_map.item_list_map,
            matched: updated_matched_item_map,
          };
        }
      }
      return updated_state;
    }

    case 'UPDATE_RESTOCK_NOTIFICATION_STATUS': {
      return {
        ...state,
        ui: {
          ...state.ui,
          restock_notification_status: action.payload,
        },
      };
    }

    case 'RESET_OPTION_VIEW': {
      const { enabledShowOnlyZigzinOption } = action.payload;

      return {
        ...state,
        ui: {
          ...state.ui,
          restock_notification_status: null,
          restock_notification_start_index: null,
          is_restock_selection_mode: false,
          is_item_card_mode_active: false,
          is_only_zigzin_option_active: enabledShowOnlyZigzinOption ?? false,
        },
        dropdown_states: state.enabled_first_dropdown_open
          ? openDropdown(state, MAIN_DROPDOWN_GROUP_INDEX, DROPDOWN_FIRST_INDEX)
          : state.dropdown_states.map((row) => row.map(() => false)),
        additional_dropdown_state: false,
        selection: initializeSelectionFromIndex(DROPDOWN_FIRST_INDEX),
        custom_input_states: resetCustomInputStates(state.custom_input_states),
        item_cards: {
          list: [],
          total_count: 0,
        },
      };
    }

    case 'RESET_RESTOCK_NOTIFICATION': {
      return {
        ...state,
        ui: {
          ...state.ui,
          restock_notification_status: null,
          restock_notification_start_index: null,
          is_restock_selection_mode: false,
        },
        dropdown_states: toggleDropdown(state, MAIN_DROPDOWN_GROUP_INDEX, DROPDOWN_FIRST_INDEX),
        selection: initializeSelectionFromIndex(DROPDOWN_FIRST_INDEX),
      };
    }

    case 'UPDATE_CUSTOM_INPUT_STATE': {
      const { name, updates } = action.payload;
      const newCustomInputStates = new Map(state.custom_input_states);
      const currentState = newCustomInputStates.get(name);

      if (currentState) {
        newCustomInputStates.set(name, { ...currentState, ...updates });
      }

      return {
        ...state,
        custom_input_states: newCustomInputStates,
      };
    }

    case 'RESET_ALL_CUSTOM_INPUTS': {
      return {
        ...state,
        custom_input_states: resetCustomInputStates(state.custom_input_states),
      };
    }

    default:
      return state;
  }
};

const initializeSelectionFromIndex = (selected_dropdown_index: number): SelectionState => {
  return {
    selected_group_index: MAIN_DROPDOWN_GROUP_INDEX,
    selected_dropdown_index,
    metadata: {
      selected_option_name_list: [],
      selected_zigzin_option_id_list: [],
      selected_general_option_id_list: [],
      last_selected_shipping_type: null,
      previous_group_selections: [],
    },
  };
};

const openDropdown = (state: ProductOptionState, group_index: number, dropdown_index: number): boolean[][] => {
  const updated_dropdown_states = state.dropdown_states.map((row) => row.map(() => false));
  updated_dropdown_states[group_index][dropdown_index] = true;
  return updated_dropdown_states;
};

const toggleDropdown = (state: ProductOptionState, group_index: number, dropdown_index: number): boolean[][] => {
  const updated_dropdown_states = state.dropdown_states.map((row) => row.map(() => false));
  updated_dropdown_states[group_index][dropdown_index] = !state.dropdown_states[group_index][dropdown_index];
  return updated_dropdown_states;
};

/**
 * 메타데이터의 선택된 옵션 리스트들을 지정된 인덱스까지 자르는 헬퍼 함수
 */
const sliceMetadataLists = (
  metadata: SelectionState['metadata'],
  dropdown_index: number,
  previous_group_selection?: SelectionState['metadata'],
) => {
  if (previous_group_selection) {
    // 이전 그룹 선택 정보에서 복원
    metadata.selected_option_name_list =
      previous_group_selection.selected_option_name_list.slice(0, dropdown_index) ?? [];
    metadata.selected_zigzin_option_id_list =
      previous_group_selection.selected_zigzin_option_id_list.slice(0, dropdown_index) ?? [];
    metadata.selected_general_option_id_list =
      previous_group_selection.selected_general_option_id_list.slice(0, dropdown_index) ?? [];
    metadata.last_selected_shipping_type = previous_group_selection.last_selected_shipping_type ?? null;
  } else {
    // 현재 메타데이터에서 자르기
    metadata.selected_option_name_list = metadata.selected_option_name_list.slice(0, dropdown_index);
    metadata.selected_zigzin_option_id_list = metadata.selected_zigzin_option_id_list.slice(0, dropdown_index);
    metadata.selected_general_option_id_list = metadata.selected_general_option_id_list.slice(0, dropdown_index);
    metadata.last_selected_shipping_type = dropdown_index > 0 ? metadata.last_selected_shipping_type : null;
  }
};

const resetCustomInputStates = (custom_input_states: Map<string, CustomInputState>): Map<string, CustomInputState> => {
  const newCustomInputStates = new Map(custom_input_states);
  newCustomInputStates.forEach((inputState, name) => {
    newCustomInputStates.set(name, {
      ...inputState,
      value: '',
      focused: false,
    });
  });
  return newCustomInputStates;
};
